import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts';
import { SERIES_PALETTE, axisTick } from './chartTheme';
import { chartTooltipFormatter, chartTooltipProps } from './ChartTooltip';
import i18n from '../../i18n';

function truncateLabel(value, max = 14) {
  const s = String(value ?? '');
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

/**
 * Vertical or horizontal comparison bars.
 * data: [{ name, value }] or multi-key rows with series[].
 * layout: 'vertical' (default) | 'horizontal'
 */
export default function CompareBar({
  data = [],
  series = [{ dataKey: 'value', name: i18n.t('charts.defaultSeries', { defaultValue: 'Value' }) }],
  xKey = 'name',
  height = 220,
  layout = 'vertical',
  showLegend = false,
  colorByIndex = true,
  yFormatter,
  labelMaxLen = 14,
}) {
  const rows = useMemo(() => {
    if (!data?.length) return [];
    return data.map((row) => ({
      ...row,
      [xKey]: truncateLabel(row[xKey], labelMaxLen),
      __fullName: row[xKey],
    }));
  }, [data, xKey, labelMaxLen]);

  if (!rows.length) return null;
  const horizontal = layout === 'horizontal';
  const categoryWidth = Math.min(120, 8 + Math.max(...rows.map((r) => String(r[xKey] || '').length)) * 7);
  const sampleVals = rows.flatMap((row) => series.map((s) => Number(row[s.dataKey]) || 0));
  const maxAbs = sampleVals.length ? Math.max(...sampleVals.map((v) => Math.abs(v))) : 0;
  const allIntegers = sampleVals.every((v) => Number.isInteger(v));
  const sampleLabel = yFormatter ? String(yFormatter(maxAbs || 0)) : String(Math.round(maxAbs || 0));
  const valueAxisWidth = Math.min(110, Math.max(56, 16 + sampleLabel.length * 8));
  const yWidth = horizontal ? categoryWidth : valueAxisWidth;
  const chartHeight = horizontal
    ? Math.max(height, 40 + rows.length * 34)
    : height;
  const tickFmt = (v) => {
    const n = Number(v);
    const shown = allIntegers && Number.isFinite(n) ? Math.round(n) : v;
    if (!yFormatter) return shown;
    return String(yFormatter(shown)).replace(/\s/g, '\u00A0');
  };
  // Tight integer domain — avoid silly headroom (e.g. 0–4 when max is 1).
  const intDomain = allIntegers
    ? [0, (dataMax) => Math.max(1, Math.ceil(Number(dataMax) || 0))]
    : undefined;

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        data={rows}
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 8, right: 16, left: 4, bottom: horizontal ? 4 : 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={!horizontal} vertical={horizontal} />
        {horizontal ? (
          <>
            <XAxis
              type="number"
              tick={axisTick}
              axisLine={false}
              tickLine={false}
              tickFormatter={tickFmt}
              allowDecimals={!allIntegers}
              domain={intDomain}
            />
            <YAxis
              type="category"
              dataKey={xKey}
              tick={axisTick}
              axisLine={false}
              tickLine={false}
              width={yWidth}
            />
          </>
        ) : (
          <>
            <XAxis dataKey={xKey} tick={axisTick} axisLine={false} tickLine={false} interval={0} />
            <YAxis
              tick={axisTick}
              axisLine={false}
              tickLine={false}
              width={yWidth}
              tickFormatter={tickFmt}
              allowDecimals={!allIntegers}
              domain={intDomain}
            />
          </>
        )}
        <Tooltip
          {...chartTooltipProps}
          labelFormatter={(_, payload) => payload?.[0]?.payload?.__fullName ?? _}
          formatter={chartTooltipFormatter(yFormatter)}
        />
        {showLegend || series.length > 1 ? (
          <Legend wrapperStyle={{ fontSize: 12 }} />
        ) : null}
        {series.map((s, si) => (
          <Bar
            key={s.dataKey}
            dataKey={s.dataKey}
            name={s.name || s.dataKey}
            fill={s.color || SERIES_PALETTE[si % SERIES_PALETTE.length]}
            radius={horizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]}
            maxBarSize={40}
          >
            {colorByIndex && series.length === 1
              ? rows.map((_, i) => (
                  <Cell key={i} fill={SERIES_PALETTE[i % SERIES_PALETTE.length]} />
                ))
              : null}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
